import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { AnyFieldApi } from '@tanstack/react-form';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { FormPageLayout } from '../../../../components/layout/FormPageLayout';
import { Input, FormField } from '../../../../components/ui/FormField';
import { FormSelect } from '../../../../components/ui/FormSelect';
import { useToast } from '../../../../components/ui/Toast';
import { Money } from '../../../../components/ui/Money';
import { z } from 'zod';
import { Decimal } from 'decimal.js';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../../lib/locale';
import { useAdvancesList, useRecordAdvancePayment } from '../../../../features/finance/hooks';
import { Banner } from '../../../../components/ui/Banner';

export const Route = createFileRoute('/_auth/finance/advances/new')({
  validateSearch: z.object({
    advanceId: z.string().optional(),
  }),
  component: RecordAdvancePaymentPage,
});

interface AdvanceRecord {
  id: string;
  subscriberName: string | null;
  ticketNumber: number | null;
  amountPaise: number;
  paidPaise: number;
  interestRateAnnualBps: number | null;
  daysPending: number;
  accruedInterestPaise: number;
  status: 'OPEN' | 'PARTIALLY_PAID' | 'FULLY_PAID';
}

const advanceSchema = z.object({
  payAmountRupees: z.string().min(1, 'Amount is required'),
  paymentMode: z.enum(['CASH', 'BANK_TRANSFER', 'CHEQUE']),
  particulars: z.string().min(1, 'Particulars are required').max(500),
  entryDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Valid date required'),
  notes: z.string().max(500).optional(),
});

type AdvanceForm = z.infer<typeof advanceSchema>;

const advanceFormConfig = defineFormPage<AdvanceForm>({
  resource: 'advances',
  defaultValues: {
    payAmountRupees: '',
    paymentMode: 'CASH',
    particulars: '',
    entryDate: new Date().toISOString().split('T')[0],
    notes: '',
  },
  schema: advanceSchema,
});

function RecordAdvancePaymentPage() {
  const navigate = useNavigate();
  const { advanceId } = Route.useSearch();
  const { toast } = useToast();
  const { data: response } = useAdvancesList();
  const records = (response?.data ?? []) as AdvanceRecord[];
  const selectedRecord = records.find(r => r.id === advanceId);
  const recordPaymentMutation = useRecordAdvancePayment();

  const {
    form,
    isSubmitting,
    handleSubmit,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(advanceFormConfig, recordPaymentMutation, {
    routeId: `finance/advances/new/${advanceId || 'general'}`,
    onBeforeSubmit: (value) => {
      if (!selectedRecord) throw new Error('No valid bridge advance record selected.');
      const payRupees = new Decimal(value.payAmountRupees);
      if (payRupees.isNaN() || payRupees.lte(0)) throw new Error('Please enter a valid amount.');
      const payPaise = payRupees.mul(100).round().toNumber();
      const desc = payRupees.toNumber().toLocaleString(DEFAULT_LOCALE, { style: 'currency', currency: DEFAULT_CURRENCY });
      return {
        id: selectedRecord.id,
        amountPaise: payPaise,
        paymentMode: value.paymentMode,
        particulars: value.particulars,
        entryDate: value.entryDate,
        notes: value.notes || undefined,
        _description: desc,
      } as never;
    },
    onSuccess: () => {
      toast({
        type: 'success',
        title: 'Payment Recorded',
        description: 'Successfully credited towards subscriber outstanding advance.',
      });
      void navigate({ to: '/finance/advances' });
    },
    onError: (err: { message?: string }) => {
      toast({
        type: 'error',
        title: 'Failed to Record Payment',
        description: err?.message || 'Could not record the advance payment. Please try again.',
      });
    },
  });

  return (
    <FormPageLayout
      title="Record Advance Payment"
      subtitle="Issue a short-term advance to a member against future collections or dividend payouts."
      backTo="/finance/advances"
      variant="card"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
            {hasDraft && (
              <Banner
                variant="info"
                title="Restore Draft"
                description="You have an unsaved draft from a previous session. Would you like to restore it?"
                action={{
                  label: 'Restore',
                  onClick: () => {
                    const draft = restoreDraft();
                    if (draft) {
                      Object.entries(draft).forEach(([key, val]) => {
                        form.setFieldValue(key as never, val as never);
                      });
                    }
                    dismissDraft();
                  },
                }}
                secondaryAction={{
                  label: 'Discard',
                  onClick: () => {
                    clearDraft();
                  },
                }}
              />
            )}

            {selectedRecord ? (
              <div className="bg-surface-2 p-3 rounded-lg border border-border flex flex-col gap-1 mb-4">
                <p className="text-xs text-text-muted font-semibold">Subscriber Outstanding</p>
                <p className="text-sm font-bold text-text-primary">
                  {selectedRecord.subscriberName ?? 'Unlinked advance'} {selectedRecord.ticketNumber ? `(Ticket ${selectedRecord.ticketNumber})` : ''}
                </p>
                <div className="text-xs flex items-center gap-1.5 mt-1 font-semibold text-danger-500">
                  Principal: <Money paise={selectedRecord.amountPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                </div>
              </div>
            ) : (
              <div className="bg-danger-bg/10 border border-danger-bg p-3 rounded-lg text-xs text-danger-text">
                No bridge advance selected. Please go back to the list and click "Record Pay" on an active record.
              </div>
            )}

            <form.Field
              name="payAmountRupees"
              validators={{ onChange: ({ value }: { value: any }) => advanceSchema.shape.payAmountRupees.safeParse(value).success ? undefined : advanceSchema.shape.payAmountRupees.safeParse(value).error?.issues[0]?.message }}
              children={(field: any) => (
                <FormField label="Payment Received (Rupees)" required>
                  <Input
                    type="number"
                    placeholder="e.g. 15000"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={!selectedRecord || isSubmitting}
                  />
                </FormField>
              )}
            />

            <form.Field
              name="paymentMode"
              validators={{ onChange: ({ value }: { value: unknown }) => advanceSchema.shape.paymentMode.safeParse(value).success ? undefined : advanceSchema.shape.paymentMode.safeParse(value).error?.issues[0]?.message }}
              children={(field: AnyFieldApi) => (
                <FormSelect
                  label="Payment Mode"
                  required
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={!selectedRecord || isSubmitting}
                  options={[
                    { value: 'CASH', label: 'Cash' },
                    { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
                    { value: 'CHEQUE', label: 'Cheque' },
                  ]}
                />
              )}
            />

            <form.Field
              name="entryDate"
              validators={{ onChange: ({ value }: { value: unknown }) => advanceSchema.shape.entryDate.safeParse(value).success ? undefined : advanceSchema.shape.entryDate.safeParse(value).error?.issues[0]?.message }}
              children={(field: AnyFieldApi) => (
                <FormField label="Entry Date" required>
                  <Input
                    type="date"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={!selectedRecord || isSubmitting}
                  />
                </FormField>
              )}
            />

            <form.Field
              name="particulars"
              validators={{ onChange: ({ value }: { value: unknown }) => advanceSchema.shape.particulars.safeParse(value).success ? undefined : advanceSchema.shape.particulars.safeParse(value).error?.issues[0]?.message }}
              children={(field: AnyFieldApi) => (
                <FormField label="Particulars" required>
                  <Input
                    type="text"
                    placeholder="e.g. Cash receipt towards bridge advance"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={!selectedRecord || isSubmitting}
                  />
                </FormField>
              )}
            />

             <form.Field
              name="notes"
              children={(field: AnyFieldApi) => (
                <FormField label="Additional Notes">
                  <textarea
                    className="w-full h-24 p-3 border border-border rounded-lg bg-surface-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent-500"
                    placeholder="Enter additional repayment details or notes..."
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={!selectedRecord || isSubmitting}
                  />
                </FormField>
              )}
            />

            <FormActions onCancel={() => void navigate({ to: '/finance/advances' })} isSubmitting={isSubmitting} submitLabel="Confirm Payment" />
          </form>
    </FormPageLayout>
  );
}
